import { Injectable, inject } from '@angular/core';
import { DeviceDetectorService } from 'ngx-device-detector';
import { pki, util, md as forgeMd } from 'node-forge';
import { AppConfigService } from '@services/app-configuration.service';
import { StorageService } from '@services/storage.service';
import { EventManagerService } from '@services/event-manager.service';
import { AppLoggerService } from '@services/app-logger.service';
import { defaultConfig } from 'src/app/app.config';

const { configLoaded, compromisedDevice: _compromisedDevice } = defaultConfig.appEvents;

const _EVENT_ROOTED_DEVICE = 'rootedDevice';
const _EVENT_VIRTUAL_DEVICE = 'virtualDevice';
const _EVENT_DEVELOPER_MODE = 'developerMode';

const DEVICE_PREFFIX_ATTRIBUTE = 'devicePreffix';

interface HardwareInfo {
  deviceId: string;
  name: string;
  androidSDKVersion: string;
  isVirtual: boolean;
  manufacturer: string;
  model: string;
  operatingSystem: string;
  osVersion: string;
  platform: string;
  webViewVersion: string;
}

interface DeviceKeys {
  privateKey: pki.rsa.PrivateKey;
  publicKey: pki.rsa.PublicKey;
  platformKey?: pki.rsa.PublicKey;
}

interface DeviceInfo {
  deviceId: string;
  deviceNativeAlias: string;
  model: string;
  platform: string;
  operatingSystem: string;
  osVersion: string;
  manufacturer: string;
  name: string;
  isVirtual: boolean;
  webViewVersion: string;
  androidSDKVersion: string;
}

@Injectable({
  providedIn: 'root',
})
export class DeviceManagementService {
  private _storageService = inject(StorageService);
  private _appConfig = inject(AppConfigService);
  private _deviceService = inject(DeviceDetectorService);
  private _eventManager = inject(EventManagerService);

  private _logger = inject(AppLoggerService);
  deviceInfo: DeviceInfo | null;
  deviceId: string;
  publicKey: string;
  devicePreffix: string;
  deviceKeys: DeviceKeys | null;
  hardwareInfo: HardwareInfo;
  isRooted = false;
  isVirtual = false;
  isDeveloperMode = false;

  constructor() {
    this._eventManager.subscribe(configLoaded.name, () => this.startDeviceService());
  }

  async startDeviceService() {
    this.devicePreffix = this._appConfig.getParameter(DEVICE_PREFFIX_ATTRIBUTE);
    this.obtainHardwareInfo();
  }

  async obtainHardwareInfo() {
    const deviceInfo = this._deviceService.getDeviceInfo();
    const isDesktopDevice = this._deviceService.isDesktop();
    this.hardwareInfo = {
      deviceId: deviceInfo?.browser_version,
      name: `${deviceInfo?.os} ${deviceInfo?.browser}`,
      androidSDKVersion: 'none',
      isVirtual: true,
      manufacturer: deviceInfo?.browser,
      model: deviceInfo?.browser_version,
      operatingSystem: deviceInfo?.os,
      osVersion: deviceInfo?.os_version,
      platform: isDesktopDevice ? 'desktop' : 'mobile',
      webViewVersion: deviceInfo?.userAgent,
    };
  }

  getHardwareInfo() {
    return this.hardwareInfo;
  }

  async activateDeviceSecurityValidation() {
    return null;
  }

  public async createKeyPair() {
    let keyPair;
    let keyPairPem;
    await pki.rsa.generateKeyPair(2048, 0x10001, (err, keypair) => {
      keyPair = keypair;
      keyPairPem = {
        publicKey: pki.publicKeyToPem(keypair.publicKey),
        privateKey: pki.privateKeyToPem(keypair.privateKey),
      };
    });
    return { keyPairPem, keyPair };
  }

  public async setAppDevice(deviceId, platformKey) {
    this.deviceId = deviceId;
    const { keyPair, keyPairPem } = await this.createKeyPair();
    this.deviceKeys = keyPair;
    this.publicKey = keyPairPem.publicKey;
    this._storageService.setItem('devices', `${this.devicePreffix}-DEVICE-ID`, deviceId);
    this._storageService.setItem('devices', `${this.devicePreffix}-DEVICE-${deviceId}`, {
      deviceId,
      platformKey,
      ...keyPairPem,
    });
  }

  public async loadAppDevice() {
    this.deviceId = await this._storageService.getItem('devices', `${this.devicePreffix}-DEVICE-ID`);
    if (!this.deviceId) {
      return null;
    }
    const {
      deviceId,
      model,
      platform,
      operatingSystem,
      osVersion,
      manufacturer,
      name,
      isVirtual,
      webViewVersion,
      androidSDKVersion,
    } = this.hardwareInfo;
    this.deviceInfo = {
      deviceId: this.deviceId,
      deviceNativeAlias: deviceId,
      model,
      platform,
      operatingSystem,
      osVersion,
      manufacturer,
      name,
      isVirtual,
      webViewVersion,
      androidSDKVersion,
    };
    if (!this.deviceKeys) {
      const deviceKey = `${this.devicePreffix}-DEVICE-${this.deviceId}`;
      const data = await this._storageService.getItem<{ privateKey: string; publicKey: string; platformKey: string }>(
        'devices',
        deviceKey
      );
      const { privateKey, publicKey, platformKey } =
        data ?? ({} as Partial<{ privateKey: string; publicKey: string; platformKey: string }>);
      if (privateKey && publicKey && platformKey) {
        this.deviceKeys = {
          privateKey: pki.privateKeyFromPem(privateKey),
          publicKey: pki.publicKeyFromPem(publicKey),
          platformKey: pki.publicKeyFromPem(platformKey),
        };
        this.publicKey = publicKey;
      }
    }
    return { deviceInfo: this.deviceInfo };
  }

  public async getAppDevice() {
    if (!this.deviceInfo || !this.deviceKeys) {
      await this.loadAppDevice();
    }
    const devicePublicInfo = {
      deviceId: this.deviceId,
      deviceData: this.deviceInfo,
      publicKey: this.publicKey,
    };
    devicePublicInfo?.deviceData && delete devicePublicInfo.deviceData.deviceId;
    return devicePublicInfo;
  }

  public async getDeviceId() {
    await this.getAppDevice();
    return this.deviceId;
  }

  public async removeDevice() {
    const deviceId = await this._storageService.getItem('devices', `${this.devicePreffix}-DEVICE-ID`);
    if (deviceId) {
      this._storageService.removeItem('devices', `${this.devicePreffix}-DEVICE-ID`);
      this._storageService.removeItem('devices', `${this.devicePreffix}-DEVICE-${deviceId}`);
      this.deviceInfo = null;
      this.deviceId = null;
      this.publicKey = null;
      this.deviceKeys = null;
    }
  }

  public async encryptData(data) {
    if (!this.deviceInfo || !this.deviceKeys) {
      await this.loadAppDevice();
    }
    const { publicKey } = this.deviceKeys;
    return publicKey.encrypt(data);
  }

  public async decryptData(data) {
    if (!this.deviceInfo || !this.deviceKeys) {
      await this.loadAppDevice();
    }
    const { privateKey } = this.deviceKeys ?? {};
    if (!privateKey) {
      return '';
    }
    try {
      const rawData = util.decode64(data);
      return privateKey ? privateKey.decrypt(rawData) : null;
    } catch (e) {
      this._logger.error('[DeviceManagement] Error al descifrar datos:', e);
      return null;
    }
  }

  public async signData(data) {
    if (!this.deviceInfo || !this.deviceKeys) {
      await this.loadAppDevice();
    }
    const { privateKey } = this.deviceKeys ?? {};
    if (!privateKey) {
      return null;
    }
    try {
      const digest = forgeMd.sha256.create();
      digest.update(data);
      const sign = privateKey.sign(digest);
      const signedData = util.encode64(sign);
      return signedData;
    } catch (e) {
      this._logger.error('[DeviceManagement] Error al firmar datos:', e);
      return null;
    }
  }
}
